import React, { useRef, useEffect } from 'react';
import { AnimatedProgress } from '../../../../../components/ui/animated-progress';
import {
  Zap,
  FileText,
  Scan,
  Brain,
  Layers,
  FileSpreadsheet,
  CheckCircle2,
  Loader2,
  Activity,
  Shield,
  Database,
  ListTree,
  ClipboardList,
  GitBranch,
} from 'lucide-react';
import type { Step, ProcessingStep, ParserLogEntry, ProcessingStats } from '../types';

interface ProcessingPhaseProps {
  steps: Step[];
  currentStep: number;
  processingSteps: ProcessingStep[];
  selectedFile: File | null;
  processingProgress: number;
  parserLog: ParserLogEntry[];
  stats: ProcessingStats | null;
  statsLoading: boolean;
}

const stepIcons = [
  <FileText className="w-3 h-3" />,
  <Scan className="w-3 h-3" />,
  <Brain className="w-3 h-3" />,
  <Layers className="w-3 h-3" />,
  <FileSpreadsheet className="w-3 h-3" />,
];

export const ProcessingPhase: React.FC<ProcessingPhaseProps> = ({
  steps,
  currentStep,
  processingSteps,
  selectedFile,
  processingProgress,
  parserLog,
  stats,
  statsLoading,
}) => {
  const parserLogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (parserLogRef.current) {
      parserLogRef.current.scrollTop = parserLogRef.current.scrollHeight;
    }
  }, [parserLog]);

  // Extract quick stats from log data
  const lastData = [...parserLog].reverse().find((e) => e.data)?.data;
  const scopeCount = lastData?.scopeCount || lastData?.scopes?.length || parserLog.find((e) => e.data?.scopeCount)?.data?.scopeCount || '-';
  const sectionCount = lastData?.sectionCount || lastData?.sectionsProcessed || parserLog.find((e) => e.data?.sectionCount)?.data?.sectionCount || '-';
  const reqCount = lastData?.totalRequirements || parserLog.find((e) => e.data?.totalRequirements)?.data?.totalRequirements || '-';
  const subReqCount = lastData?.totalSubRequirements || parserLog.find((e) => e.data?.totalSubRequirements)?.data?.totalSubRequirements || '-';

  return (
    <div className="h-[calc(100vh-7rem)] flex flex-col p-3 lg:p-4 max-w-6xl mx-auto overflow-hidden">
      {/* Row 1: Header + Progress */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2 shrink-0">
        <div className="flex items-center gap-2.5 shrink-0">
          <div className="p-1.5 bg-blue-600 rounded-lg">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-base font-bold text-slate-900 leading-tight">Processing</h1>
            <p className="text-[10px] text-slate-500 truncate max-w-[200px]">{selectedFile?.name}</p>
          </div>
          <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 shrink-0 sm:hidden ml-auto">
            {processingProgress}%
          </span>
        </div>

        {/* Progress bar */}
        <div className="flex-1 min-w-0">
          <AnimatedProgress value={processingProgress} size="sm" variant="info" showLabel animated striped />
        </div>

        <span className="hidden sm:inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 shrink-0">
          {processingProgress}%
        </span>
        <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 shrink-0">
          <Shield className="w-3 h-3" /> Encrypted
        </span>
      </div>

      {/* Row 2: Stepper dots + Step chips */}
      <div className="flex items-center gap-2 sm:gap-3 mb-2 shrink-0 overflow-x-auto">
        <div className="hidden sm:flex items-center gap-1.5 shrink-0">
          {steps.map((step, idx) => (
            <div
              key={step.id}
              className={`w-2 h-2 rounded-full ${
                idx < currentStep ? 'bg-emerald-500' : idx === currentStep ? 'bg-blue-500 animate-pulse' : 'bg-slate-300'
              }`}
              title={step.label}
            />
          ))}
        </div>
        <div className="hidden sm:block w-px h-4 bg-slate-200 shrink-0" />

        <div className="flex gap-1.5 flex-1 min-w-0">
          {processingSteps.map((step, index) => {
            const stepProgress = (index + 1) * 20;
            const isActive = processingProgress >= stepProgress;
            const isCurrent = processingProgress >= stepProgress - 20 && processingProgress < stepProgress;

            return (
              <div
                key={index}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] transition-all ${
                  isActive
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : isCurrent
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : 'border-slate-200 bg-slate-50 text-slate-500'
                }`}
              >
                <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${
                  isActive ? 'text-emerald-600' : isCurrent ? 'text-blue-600' : 'text-slate-400'
                }`}>
                  {isActive ? <CheckCircle2 className="w-3 h-3" /> : isCurrent ? <Loader2 className="w-3 h-3 animate-spin" /> : stepIcons[index]}
                </div>
                <span className="font-medium truncate">{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Row 3: Log + Right Sidebar */}
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-3">
        {/* Log panel */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-1.5">
            <h4 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <Activity className="w-3 h-3 text-blue-600 animate-pulse" />
              Live Analysis
            </h4>
            {parserLog.length > 0 && (
              <span className="text-[10px] text-slate-400">{parserLog.length} events</span>
            )}
          </div>
          <div
            ref={parserLogRef}
            className="bg-slate-900 rounded-lg p-3 flex-1 min-h-0 overflow-y-auto font-mono text-[11px] space-y-1 border border-slate-700"
          >
            {parserLog.length === 0 ? (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Waiting for pipeline...
              </div>
            ) : (
              parserLog.map((entry, i) => (
                <div key={i} className="flex gap-2 leading-relaxed">
                  <span className="text-slate-500 shrink-0">{entry.timestamp}</span>
                  <span className={`shrink-0 font-bold ${
                    entry.step === 'complete' ? 'text-emerald-400' : entry.step === 'error' ? 'text-red-400' : 'text-blue-400'
                  }`}>
                    [{entry.step}]
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-slate-200">{entry.message}</span>
                    {entry.detail && (
                      <div className="text-slate-500 text-[10px] mt-0.5 truncate">{entry.detail}</div>
                    )}
                  </div>
                </div>
              ))
            )}
            {processingProgress > 0 && processingProgress < 100 && (
              <div className="flex gap-2 animate-pulse mt-1">
                <span className="text-slate-500">{new Date().toLocaleTimeString()}</span>
                <span className="text-yellow-400 font-bold">[working]</span>
                <span className="text-slate-400">Processing... ({processingProgress}%)</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-full lg:w-52 shrink-0 flex flex-col sm:flex-row lg:flex-col gap-2">
          {/* Discovered Structure */}
          <div className="bg-white border border-slate-200 rounded-xl p-2.5">
            <h4 className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Discovered</h4>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs text-slate-600">Scopes</span>
                </div>
                <span className="text-sm font-bold text-blue-700">{scopeCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ListTree className="w-3.5 h-3.5 text-indigo-500" />
                  <span className="text-xs text-slate-600">Sections</span>
                </div>
                <span className="text-sm font-bold text-indigo-700">{sectionCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-3.5 h-3.5 text-purple-500" />
                  <span className="text-xs text-slate-600">Requirements</span>
                </div>
                <span className="text-sm font-bold text-purple-700">{reqCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GitBranch className="w-3.5 h-3.5 text-violet-500" />
                  <span className="text-xs text-slate-600">Sub-Reqs</span>
                </div>
                <span className="text-sm font-bold text-violet-700">{subReqCount}</span>
              </div>
            </div>
          </div>

          {/* Global Stats */}
          <div className="bg-white border border-slate-200 rounded-xl p-2.5">
            <h4 className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Platform Stats</h4>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between py-1 px-2 bg-blue-50 rounded text-[11px]">
                <span className="text-slate-600">Processed</span>
                <span className="font-bold text-blue-700">{statsLoading ? '...' : (stats?.documentsProcessed ?? 0)}</span>
              </div>
              <div className="flex items-center justify-between py-1 px-2 bg-emerald-50 rounded text-[11px]">
                <span className="text-slate-600">Completed</span>
                <span className="font-bold text-emerald-700">{statsLoading ? '...' : (stats?.completed ?? 0)}</span>
              </div>
              <div className="flex items-center justify-between py-1 px-2 bg-purple-50 rounded text-[11px]">
                <span className="text-slate-600">Success</span>
                <span className="font-bold text-purple-700">{statsLoading ? '...' : `${stats?.successRate ?? 0}%`}</span>
              </div>
            </div>
          </div>

          {/* Engine */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5">
            <div className="flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-indigo-600" />
              <span className="text-[10px] font-bold text-indigo-700">Vastabloc</span>
              <span className="text-[9px] text-slate-400 ml-auto">Yiontech</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
